// src/components/ResultsDashboard.tsx

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AssessmentResult } from '../types';
import NavigatorNav from './NavigatorNav';
import { UnknownAnswersWidget } from './UnknownAnswersWidget';
import { CriticalGapsWarning } from './CriticalGapsWarning';
import { LegalDisclaimer } from './LegalDisclaimer';

interface ResultsDashboardProps {
  result: AssessmentResult;
  userEmail: string;
  onRestart: () => void;
}

const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ result, userEmail, onRestart }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'gaps' | 'roadmap'>('overview');

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'LOW':
        return 'text-green-600 bg-green-100';
      case 'MODERATE':
        return 'text-yellow-600 bg-yellow-100';
      case 'HIGH':
        return 'text-orange-600 bg-orange-100';
      case 'CRITICAL':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'good':
        return '✅';
      case 'needs-work':
        return '⚠️';
      case 'critical':
        return '❌';
      default:
        return '•';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'border-red-500 bg-red-50';
      case 'HIGH':
        return 'border-orange-500 bg-orange-50';
      case 'MODERATE':
        return 'border-yellow-500 bg-yellow-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <NavigatorNav />
      <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-4xl font-bold text-gray-900">
              Your HIPAA Compliance Assessment
            </h1>
            <LegalDisclaimer type="badge" />
          </div>
          <p className="text-lg text-gray-600">
            Results sent to {userEmail}
          </p>
        </motion.div>

        {/* Post-Assessment Disclaimer */}
        <LegalDisclaimer type="post-assessment" />

        {/* Critical Gaps Warning - ABOVE Score Card */}
        {result.criticalGaps && result.criticalGaps.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <CriticalGapsWarning gaps={result.criticalGaps} />
          </motion.div>
        )}

        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-6"
        >
          <div className="text-center">
            <div className="mb-4">
              <div className="text-7xl font-bold text-gray-900 mb-2">
                {result.percentage}%
              </div>
              <div className="text-2xl text-gray-600 mb-4">
                {result.totalScore} / {result.maxScore} points
              </div>
              <span className={`inline-block px-6 py-3 rounded-full text-lg font-bold ${getRiskLevelColor(result.riskLevel)}`}>
                {result.riskLevel === 'LOW' && '✓ Low Risk'}
                {result.riskLevel === 'MODERATE' && '⚠️ Moderate Risk'}
                {result.riskLevel === 'HIGH' && '⚠️ High Risk'}
                {result.riskLevel === 'CRITICAL' && '🚨 Critical Risk'}
              </span>
            </div>

            {result.riskLevel !== 'LOW' && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2">
                  📈 <strong>Projected Improvement:</strong>
                </p>
                <p className="text-lg font-medium text-blue-600">
                  {result.projectedImprovement.currentScore}% → {result.projectedImprovement.projectedScore}% in {result.projectedImprovement.timeframe}
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Unknown Answers Widget */}
        {result.hasUnknowns && result.unknownAnswers && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <UnknownAnswersWidget
              unknownAnswers={result.unknownAnswers}
              scoreImprovement={result.scoreIfUnknownsResolved}
            />
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'gaps', 'roadmap'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as typeof activeTab)}
              className={`px-6 py-3 rounded-lg font-medium transition ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab === 'overview' && '📊 Overview'}
              {tab === 'gaps' && '⚠️ Gaps'}
              {tab === 'roadmap' && '🗺️ Roadmap'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Category Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Score Breakdown by Category
              </h2>
              <div className="space-y-4">
                {result.categoryScores.map((category, index) => (
                  <motion.div
                    key={category.category}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getStatusEmoji(category.status)}</span>
                        <span className="font-medium text-gray-900">{category.category}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">
                        {category.percentage}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            category.status === 'good'
                              ? 'bg-green-500'
                              : category.status === 'needs-work'
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${category.percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">
                        {category.score} / {category.maxScore}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Strengths */}
            {result.strengths.length > 0 && (
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-green-900 mb-4">
                  ✅ What You're Doing Well
                </h3>
                <div className="space-y-2">
                  {result.strengths.map((strength, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <div>
                        <p className="font-medium text-green-900">{strength.title}</p>
                        <p className="text-sm text-green-700">{strength.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'gaps' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Critical Gaps Requiring Action
            </h2>
            <p className="text-gray-600 mb-6">
              {result.criticalGaps.length} issues identified that need immediate attention
            </p>
            <div className="space-y-4">
              {result.criticalGaps.map((gap, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`border-l-4 ${getSeverityColor(gap.severity)} rounded-lg p-4`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{gap.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      gap.severity === 'CRITICAL'
                        ? 'bg-red-200 text-red-800'
                        : gap.severity === 'HIGH'
                        ? 'bg-orange-200 text-orange-800'
                        : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      {gap.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Regulation:</strong> {gap.regulation}
                  </p>
                  <p className="text-gray-700 mb-2">
                    <strong>Action:</strong> {gap.action}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>⏱️ {gap.timeframe}</span>
                    {gap.estimatedCost && <span>💰 {gap.estimatedCost}</span>}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'roadmap' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Your {result.roadmapWeeks || 30}-{result.roadmapWeeks && result.roadmapWeeks > 30 ? 'Week' : 'Day'} Compliance Roadmap
              </h2>
              <p className="text-gray-600 mb-4">
                Customized for {result.organizationSize || 'your'} organizations
              </p>
              
              {result.roadmapMetadata && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <div className="text-sm text-gray-600">Timeline</div>
                    <div className="text-xl font-bold text-blue-600">{result.roadmapMetadata.totalWeeks} weeks</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Phases</div>
                    <div className="text-xl font-bold text-blue-600">{result.roadmapMetadata.totalPhases} phases</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Est. Cost</div>
                    <div className="text-xl font-bold text-blue-600">{result.roadmapMetadata.estimatedCost}</div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {result.roadmap.map((week, weekIndex) => (
                  <motion.div
                    key={week.week}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: weekIndex * 0.1 }}
                    className={`border-l-4 ${
                      week.priority === 'CRITICAL' ? 'border-red-500' :
                      week.priority === 'HIGH' ? 'border-orange-500' : 'border-yellow-500'
                    } rounded-lg p-5 bg-gray-50`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 rounded text-xs font-bold ${
                        week.priority === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                        week.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {week.priority}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900">
                        Week {week.week}: {week.title}
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {week.tasks.map((task, taskIndex) => (
                        <div key={taskIndex} className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                              {taskIndex + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 mb-1">{task.task}</p>
                              <p className="text-sm text-gray-600 mb-2">{task.action}</p>
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                <span>⏱️ {task.estimatedTime}</span>
                                {task.owner && <span>👤 {task.owner}</span>}
                                {task.resources.map((resource, i) => (
                                  <span key={i} className="bg-blue-50 px-2 py-1 rounded">
                                    {resource}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-8 text-center text-white mt-8">
          <h2 className="text-3xl font-bold mb-4">
            Need Help Implementing This Roadmap?
          </h2>
          <p className="text-lg text-blue-100 mb-6">
            Book a free 30-minute consultation to discuss your specific compliance challenges
          </p>
          <button
            onClick={() => window.location.href = 'mailto:help@theinfinitra.com?subject=HIPAA Compliance Consultation'}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
          >
            📅 Schedule Consultation
          </button>
        </div>

        {/* Restart Button */}
        <div className="text-center mt-6">
          <button
            onClick={onRestart}
            className="text-gray-600 hover:text-gray-900 underline"
          >
            Take Assessment Again
          </button>
        </div>

        {/* Footer Branding */}
        <div className="border-t-2 border-gray-200 mt-12 pt-8 text-center bg-gray-50 rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Built by Infinitra Innovations</p>
          <p className="text-base font-semibold text-gray-900 mb-3">Saida Babu Chanda & Yogananda Karra</p>
          <div className="flex items-center justify-center gap-4 text-sm">
            <a href="https://healthtechcompliance.com/#about" className="text-blue-600 hover:text-blue-700 font-medium">
              About the Initiative
            </a>
            <span className="text-gray-400">•</span>
            <a href="https://healthtechcompliance.com/#contribute" className="text-blue-600 hover:text-blue-700 font-medium">
              Contribute
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ResultsDashboard;
